import { Injectable } from '@angular/core';
import { IM365AppDefinition } from '@office-start/app-host-types';
import { Document as DocumentReact } from '@office-start/document-types';
import { WdOriginType as WdOriginTypeReact } from '@office-start/instrumentation-types';
import { LaunchAction as LaunchActionReact } from '@office-start/launcher-types';
import { logUserAction } from '@office-start/telemetry-service';
import { Area } from '@office-start/telemetry-types';
import { take } from 'rxjs/operators';
import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { FluidHostContext } from '../../fluid-content/scopes/fluid-host-context';
import { withTimeout } from '../../shared/utilities/promise-with-timeout.utility';
import { BrandColor } from '../constants/brand-color.enum';
import { LinkTarget } from '../constants/link-target.enum';
import { OfficeRoute } from '../constants/office-route.enum';
import { WorkloadQueryParameter } from '../constants/workload-query-parameter.enum';
import { KnownInstrumentationEvents, KnownReferralPlatform } from '../instrumentation/instrumentation.service';
import { FetchStrategy } from '../instrumentation/instrumented-http.service';
import { CreateLink } from '../models/create-link.model';
import { FormsCreateLocation } from '../models/create-location.model';
import { ErrorDialogErrorCategory, ErrorDialogErrorType } from '../models/error-dialog.model';
import { FileItem, FileType } from '../models/file-item.model';
import { FileUrlModel } from '../models/file-url.model';
import { FormType } from '../models/form.model';
import { GraphError } from '../models/graph.model';
import { WdOriginType } from '../models/telemetry-constants.model';
import { BootNewDocumentUiHostFunction } from '../models/wac-host.model';
import { Workload } from '../models/workload.model';
import { MruDataService } from '../mru/mru-data.service';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { LocService } from '../shared-context/loc.service';
import { Template } from '../templates/templates.model';
import { UrlBuilder } from '../utilities/url-builder.utility';
import { UrlParser } from '../utilities/url-parser.utility';
import { InputDialogFactory } from './../input-dialog/input-dialog.factory';
import { InstrumentationService } from './../instrumentation/instrumentation.service';
import { TemplatesConfigService, TemplatesResources } from './../templates/templates-config.service';
import { AccessTokenConsumer, AccessTokenService } from './access-token.service';
import { CreateConfigService } from './create-config.service';
import docaspxFormHtmlText from './create-new-document-docaspx-form.template.html';
import loadingHtmlText from './create-new-document-loading-page.template.html';
import { CreateOneNotePageDialogService } from './create-onenote-page-dialog.service';
import { CreateStreamService } from './create-stream-content.service';
import { DocumentService } from './document.service';
import { ErrorDialogService } from './error-dialog.service';
import { FormsService } from './forms.service';
import { InputDialogService } from './input-dialog.service';
import { LocalAppLauncherService } from './local-app-launcher.service';
import newTabHtmlText from './new-tab.template.html';
import { OfficeRouteService } from './office-route.service';
import { OneDriveService } from './onedrive.service';
import { OneNoteConfigService } from './onenote-config.service';
import { OneNoteGraphService } from './onenote-graph.service';
import { ShellService } from './shell.service';
import { SwayService } from './sway.service';
import { LaunchAction, UrlLauncherService } from './url-launcher.service';
import { VroomService } from './vroom.service';
import { WacContentService } from './wac-content.service';
import { WindowService } from './window.service';
import { WorkloadsService } from './workloads.service';

interface CreateLinkCandidate {
    title: string;
    fileTypeName: string;
    appName: string;
    fileType: FileType;
    canCreateBlankDocument: boolean;
    canOpenBlankDocInNativeApp: boolean;
    canFallbackToLandingPage: boolean;
    newDocName?: string;
    fileExtension?: string;
    workloadOnlineName?: string;
    spinnerColor?: string;
    createDocAspxEnabled: boolean;
    docAspxId?: number;
    thirdPartyCreateUrl?: string;
}

interface AppManifestCreateIntent extends IM365AppDefinition {
    creationFileType: string;
    fileTypeNameLocalized?: string;
}

export const enum CreateDocumentResultType {
    GoToBlankDoc = 'GoToBlankDoc',
    GoToBlankDocInstant = 'GoToBlankDocInstant',
    GoToLanding = 'GoToLanding',
    GoToLandingFallback = 'GoToLandingFallback',
    Error = 'Error',
    CreatePending = 'CreatePending',
    Upsell = 'Upsell'
}

export const enum CreationErrorType {
    Disabled = 'Disabled', // !canCreateBlankDocument and !canFallbackToLandingPage
    Ok = 'Ok', // for successful creations
    GraphError = 'GraphError', // creations failing due to error returned from graph call
    Generic = 'Generic', // unknown cause, use debugInfo to get more information on failure
    LaunchFailed = 'LaunchFailed', // creations failing when trying to launch doc
    MultipleCreates = 'MultipleCreates', // multiple docs are being created on the same tab
    NotFound = 'NotFound' // missing url or undefined variables, use debugInfo to get more information on failure
}

export interface CreateDocumentResult {
    type: CreateDocumentResultType;
    latency: number;
    platform?: KnownReferralPlatform;
    documentUrlPromise?: Promise<string>; // WAC UI Host Specific
    errorMsg?: CreationErrorType;
    debugInfo?: string;
    httpCode?: string; //For graph calls
}

export type CreateResultHandler = (result: CreateDocumentResult) => void;

/**
 * Service that creates documents.
 */
@Injectable({
    providedIn: 'root'
})
export class CreateDocumentService {
    private readonly createLcidEnabled: boolean;
    private readonly createLinkCandidates: Map<FileType, CreateLinkCandidate>;
    private createLinksPromise: Promise<Array<CreateLink>>;
    private newTabWindow: Window;
    private readonly isPwa: boolean;
    private readonly isMsa: boolean;
    private numCreatesPending: number;
    private readonly appManifests: Map<string, AppManifestCreateIntent>;
    private readonly fluidSupportEnabled: boolean;
    private readonly wacHostApps: Set<FileType>;
    private readonly whiteboardTenantCheck: Promise<boolean>;

    private static readonly menuOrdering: Array<FileType> = [FileType.Word, FileType.PowerPoint, FileType.Excel, FileType.Forms, FileType.FormsQuiz, FileType.OneNotePage, FileType.Sway, FileType.OneNote, FileType.Visio, FileType.FluidActivityBlueprint, FileType.Fluid, FileType.Stream, FileType.ScreenRecording];
    private static readonly streamEarlierMenuOrdering: Array<FileType> = [FileType.Word, FileType.PowerPoint, FileType.Excel, FileType.Forms, FileType.FormsQuiz, FileType.OneNotePage, FileType.Sway, FileType.OneNote, FileType.Visio, FileType.Stream, FileType.ScreenRecording, FileType.FluidActivityBlueprint, FileType.Fluid];
    private static readonly fluidFirstMenuOrdering: Array<FileType> = [FileType.FluidActivityBlueprint, FileType.Fluid, FileType.Word, FileType.PowerPoint, FileType.Excel, FileType.Forms, FileType.FormsQuiz, FileType.OneNotePage, FileType.Sway, FileType.OneNote, FileType.Visio, FileType.Stream, FileType.ScreenRecording];
    private static readonly thirdPartyOrdering = []; // [FileType.Platform]; // TODO: not sure how this will look or how to decide ordering
    private static readonly firstPartyCreateIntentOrdering = [FileType.List, FileType.Designer, FileType.Yammer, FileType.PowerBI, FileType.Whiteboard];
    private static readonly combinedMenuOrdering: Array<FileType> = [FileType.Word, FileType.PowerPoint, FileType.Excel, FileType.Forms, FileType.FormsQuiz, FileType.OneNote, FileType.List, FileType.Designer, FileType.OneNotePage, FileType.Sway, FileType.Visio, FileType.FluidActivityBlueprint, FileType.Fluid, FileType.Yammer, FileType.PowerBI, FileType.Whiteboard, FileType.Stream, FileType.ScreenRecording];
    private static readonly streamEarlierCombinedMenuOrdering: Array<FileType> = [FileType.Word, FileType.PowerPoint, FileType.Excel, FileType.Forms, FileType.FormsQuiz, FileType.OneNote, FileType.List, FileType.Designer, FileType.OneNotePage, FileType.Sway, FileType.Visio, FileType.Stream, FileType.ScreenRecording, FileType.FluidActivityBlueprint, FileType.Fluid, FileType.Yammer, FileType.PowerBI, FileType.Whiteboard];
    private static readonly fluidFirstCombinedMenuOrdering: Array<FileType> = [FileType.FluidActivityBlueprint, FileType.Fluid, FileType.Word, FileType.PowerPoint, FileType.Excel, FileType.Forms, FileType.FormsQuiz, FileType.OneNote, FileType.List, FileType.Designer, FileType.OneNotePage, FileType.Sway, FileType.Visio, FileType.Yammer, FileType.PowerBI, FileType.Whiteboard, FileType.Stream, FileType.ScreenRecording];
    private static readonly fluidFirstStreamEarlierCombinedMenuOrdering: Array<FileType> = [FileType.FluidActivityBlueprint, FileType.Fluid, FileType.Word, FileType.PowerPoint, FileType.Excel, FileType.Forms, FileType.FormsQuiz, FileType.OneNote, FileType.List, FileType.Designer, FileType.OneNotePage, FileType.Sway, FileType.Visio, FileType.Stream, FileType.ScreenRecording, FileType.Yammer, FileType.PowerBI, FileType.Whiteboard];

    // Create on Home tiles are displayed in the same order as they appear in this Map
    private static readonly fileTypeToConsumerWorkloadIdMap = new Map<FileType, string>([
        [FileType.Word, 'ShellWordOnline'],
        [FileType.PowerPoint, 'ShellPowerPointOnline'],
        [FileType.Excel, 'ShellExcelOnline'],
        [FileType.Forms, 'ShellOfficeForms'],
        [FileType.FormsQuiz, 'ShellOfficeForms'],
        [FileType.List, 'ShellLists'],
        [FileType.Designer, 'Designer'],
        [FileType.Sway, 'ShellSway']
    ]);
    constructor(
        private readonly accessTokenService: AccessTokenService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly contextService: ContextService,
        private readonly createConfigService: CreateConfigService,
        private readonly createOneNotePageDialogService: CreateOneNotePageDialogService,
        private readonly createStreamService: CreateStreamService,
        private readonly documentService: DocumentService,
        private readonly errorDialogService: ErrorDialogService,
        private readonly fluidHostContext: FluidHostContext,
        private readonly formsService: FormsService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly inputDialogService: InputDialogService,
        private readonly instrumentationService: InstrumentationService,
        private readonly localAppLauncherService: LocalAppLauncherService,
        private readonly locService: LocService,
        private readonly mruDataService: MruDataService,
        private readonly officeRouteService: OfficeRouteService,
        private readonly oneDriveService: OneDriveService,
        private readonly oneNoteGraphService: OneNoteGraphService,
        private readonly oneNoteConfigService: OneNoteConfigService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly shellService: ShellService,
        private readonly swayService: SwayService,
        private readonly templatesConfigService: TemplatesConfigService,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly vroomService: VroomService,
        private readonly wacContentService: WacContentService,
        private readonly windowService: WindowService,
        private readonly workloadsService: WorkloadsService
    ) {
        const capabilities = this.capabilitiesService.capabilities;
        this.isPwa = capabilities.pwaEnabled;
        this.isMsa = this.clientStartupConfigService.config.authType === AuthType.MSA;
        this.fluidSupportEnabled = capabilities.fluidSupportEnabled;
        this.wacHostApps = new Set(capabilities.wacHostApps);

        this.numCreatesPending = 0;
        this.createLcidEnabled = this.templatesConfigService.config.createLcidEnabled;

        // The workloadOnlineName and loadingPageHexColor will need to be updated whenever the WACs update their loading page so that we are in sync.
        this.createLinkCandidates = new Map<FileType, CreateLinkCandidate>();
        const createOneDriveDocApiEnabled = capabilities.createOneDriveDocApiEnabled;
        this.createLinkCandidates.set(FileType.Word, { title: locService.CreateMenuLinkWordTitle, fileTypeName: locService.CreateModalLinkWordTitle, appName: locService.WordName, fileType: FileType.Word, newDocName: this.locService.NewWordDocumentFileName, fileExtension: 'docx', workloadOnlineName: this.locService.WordName, spinnerColor: BrandColor.Word, canCreateBlankDocument: createOneDriveDocApiEnabled, canOpenBlankDocInNativeApp: true, canFallbackToLandingPage: !capabilities.startPageApps.has(FileType.Word), createDocAspxEnabled: capabilities.createDocAspxWordEnabled, docAspxId: 1 });
        this.createLinkCandidates.set(FileType.Excel, { title: locService.CreateMenuLinkExcelTitle, fileTypeName: locService.CreateModalLinkExcelTitle, appName: locService.ExcelName, fileType: FileType.Excel, newDocName: this.locService.NewExcelWorkbookFileName, fileExtension: 'xlsx', workloadOnlineName: this.locService.ExcelName, spinnerColor: BrandColor.Excel, canCreateBlankDocument: createOneDriveDocApiEnabled, canOpenBlankDocInNativeApp: false, canFallbackToLandingPage: !capabilities.startPageApps.has(FileType.Excel), createDocAspxEnabled: capabilities.createDocAspxExcelEnabled, docAspxId: 2 });
        this.createLinkCandidates.set(FileType.PowerPoint, { title: locService.CreateMenuLinkPowerPointTitle, fileTypeName: locService.CreateModalLinkPowerPointTitle, appName: locService.PowerPointName, fileType: FileType.PowerPoint, newDocName: this.locService.NewPowerPointPresentationFileName, fileExtension: 'pptx', workloadOnlineName: this.locService.PowerPointName, spinnerColor: BrandColor.PowerPoint, canCreateBlankDocument: createOneDriveDocApiEnabled, canOpenBlankDocInNativeApp: true, canFallbackToLandingPage: !capabilities.startPageApps.has(FileType.PowerPoint), createDocAspxEnabled: capabilities.createDocAspxPowerPointEnabled, docAspxId: 3 });
        this.createLinkCandidates.set(FileType.Sway, { title: locService.CreateMenuLinkSwayTitle, fileTypeName: locService.CreateModalLinkSwayTitle, appName: locService.SwayName, fileType: FileType.Sway, workloadOnlineName: this.locService.MicrosoftSwayName, spinnerColor: BrandColor.Sway, canCreateBlankDocument: capabilities.swayCreateNewEnabled, canOpenBlankDocInNativeApp: false, canFallbackToLandingPage: !capabilities.startPageApps.has(FileType.Sway), createDocAspxEnabled: false });

        if (capabilities.fluidCreateNewEnabled) {
            this.createLinkCandidates.set(FileType.Fluid, { title: locService.CreateMenuLinkFluidTitle, fileTypeName: locService.CreateModalLinkFluidTitle, appName: locService.FluidName, fileType: FileType.Fluid, canCreateBlankDocument: this.fluidSupportEnabled, canOpenBlankDocInNativeApp: false, canFallbackToLandingPage: true, createDocAspxEnabled: false });
        }
        if (capabilities.fluidCreateAbpEnabled) {
            this.createLinkCandidates.set(FileType.FluidActivityBlueprint, { title: locService.CreateMenuLinkFluidAbpTitle, fileTypeName: locService.CreateModalLinkFluidAbpTitle, appName: locService.FluidName, fileType: FileType.FluidActivityBlueprint, canCreateBlankDocument: this.fluidSupportEnabled, canOpenBlankDocInNativeApp: false, canFallbackToLandingPage: true, createDocAspxEnabled: false });
        }

        if (capabilities.startPageApps.has(FileType.Forms)) {
            this.createLinkCandidates.set(FileType.Forms, { appName: locService.FormsName, title: locService.CreateMenuLinkFormsTitle, fileTypeName: locService.CreateModalLinkFormsFormTitle, workloadOnlineName: locService.FormsName, fileType: FileType.Forms, canCreateBlankDocument: true, canOpenBlankDocInNativeApp: false, canFallbackToLandingPage: true, createDocAspxEnabled: false, spinnerColor: BrandColor.Forms });
            this.createLinkCandidates.set(FileType.FormsQuiz, { appName: locService.FormsName, title: locService.CreateMenuLinkFormsQuizTitle, fileTypeName: locService.CreateModalLinkFormsQuizTitle, workloadOnlineName: locService.FormsName, fileType: FileType.FormsQuiz, canCreateBlankDocument: true, canOpenBlankDocInNativeApp: false, canFallbackToLandingPage: true, createDocAspxEnabled: false, spinnerColor: BrandColor.Forms });
        }

        if (capabilities.startPageApps.has(FileType.OneNote) && this.oneNoteConfigService.config.enableOneNoteApis) {
            this.createLinkCandidates.set(FileType.OneNote, { title: locService.CreateMenuLinkOneNoteTitle, fileTypeName: locService.CreateModalLinkOneNoteTitle, appName: locService.OneNoteName, fileType: FileType.OneNote, workloadOnlineName: this.locService.OneNoteName, spinnerColor: BrandColor.OneNote, canCreateBlankDocument: createOneDriveDocApiEnabled, canOpenBlankDocInNativeApp: true, canFallbackToLandingPage: false, createDocAspxEnabled: false });
        }

        if (capabilities.createOneNotePageEnabled) {
            this.createLinkCandidates.set(FileType.OneNotePage, { title: locService.CreateMenuLinkOneNotePageTitle, fileTypeName: locService.CreateModalLinkOneNotePageTitle, appName: locService.OneNoteName, fileType: FileType.OneNotePage, workloadOnlineName: this.locService.OneNoteName, spinnerColor: BrandColor.OneNote, canCreateBlankDocument: createOneDriveDocApiEnabled, canOpenBlankDocInNativeApp: true, canFallbackToLandingPage: false, createDocAspxEnabled: false });
        }

        if (capabilities.startPageApps.has(FileType.Visio)) {
            this.createLinkCandidates.set(FileType.Visio, { title: locService.CreateMenuLinkVisioTitle, fileTypeName: locService.CreateModalLinkVisioTitle, appName: locService.VisioName, fileType: FileType.Visio, newDocName: this.locService.NewVisioDrawingFileName, fileExtension: 'vsdx', workloadOnlineName: this.locService.VisioName, spinnerColor: BrandColor.Visio, canCreateBlankDocument: createOneDriveDocApiEnabled, canOpenBlankDocInNativeApp: true, canFallbackToLandingPage: false, createDocAspxEnabled: capabilities.createDocAspxVisioEnabled, docAspxId: 8 });
        } else if (capabilities.createVisioEnabled) {
            this.createLinkCandidates.set(FileType.Visio, { title: locService.CreateMenuLinkVisioTitle, fileTypeName: locService.CreateModalLinkVisioTitle, appName: locService.VisioName, fileType: FileType.Visio, newDocName: this.locService.NewVisioDrawingFileName, fileExtension: 'vsdx', workloadOnlineName: this.locService.VisioName, spinnerColor: BrandColor.Visio, canCreateBlankDocument: createOneDriveDocApiEnabled && capabilities.createDocAspxVisioEnabled, canOpenBlankDocInNativeApp: true, canFallbackToLandingPage: true, createDocAspxEnabled: capabilities.createDocAspxVisioEnabled, docAspxId: 8 });
        }

        if (capabilities.createStreamDocumentEnabled && capabilities.startPageApps.has(FileType.Stream)) {
            this.createLinkCandidates.set(FileType.Stream, { title: locService.CreateMenuLinkStreamTitle, fileTypeName: locService.CreateModalLinkStreamTitle, appName: locService.StreamName, fileType: FileType.Stream, fileExtension: 'webm', workloadOnlineName: this.locService.StreamName, spinnerColor: BrandColor.Stream, canCreateBlankDocument: createOneDriveDocApiEnabled, canOpenBlankDocInNativeApp: false, canFallbackToLandingPage: true, createDocAspxEnabled: false });
            if (capabilities.createScreenRecordingDocumentEnabled) {
                this.createLinkCandidates.set(FileType.ScreenRecording, { title: locService.CreateMenuLinkScreenRecordingTitle, fileTypeName: locService.CreateModalLinkScreenRecordingTitle, appName: locService.StreamName, fileType: FileType.ScreenRecording, fileExtension: 'webm', workloadOnlineName: this.locService.StreamName, spinnerColor: BrandColor.Stream, canCreateBlankDocument: createOneDriveDocApiEnabled, canOpenBlankDocInNativeApp: false, canFallbackToLandingPage: true, createDocAspxEnabled: false });
            }
        }

        // prefetch the OneDrive token so docaspx flow is faster
        if (capabilities.createDocumentSupported && (capabilities.createDocAspxWordEnabled || capabilities.createDocAspxExcelEnabled || capabilities.createDocAspxPowerPointEnabled)) {
            this.accessTokenService.getAccessToken(AccessTokenConsumer.OneDrive, false, this.contextService.sharedContext.oneDriveResourceId);
        }

        this.appManifests = new Map([]);
        if (capabilities.listsCreateEnabled) {
            this.appManifests.set('list', {
                name: locService.ListsName,
                creationFileType: FileType[FileType.List],
                fileTypeNameLocalized: locService.CreateFileTypeNameListsList,
                id: 'ListsAppId',
                showLoadingIndicator: true,
                staticTabs: [
                    {
                        entityId: '1',
                        name: locService.CreateMenuLinkListsTitle,
                        contentUrl:
                            this.isMsa
                                ? 'openCreate=true&workload=Office&ocid=OfficeConsumerWebCreate'
                                : 'openCreate=true&workload=Office&ocid=OfficeWebCreate', // how will we show this create param in manifest
                        websiteUrl:
                            '',
                        scopes: []
                    }
                ],
                version: '1.0.0.' //TODO: anpowe - enter correct version number
            });
        }

        if (capabilities.yammerCreateEnabled) {
            this.appManifests.set('yammer', {
                name: locService.YammerName,
                creationFileType: FileType[FileType.Yammer],
                fileTypeNameLocalized: locService.CreateFileTypeNameYammerPost,
                id: 'YammerAppId',
                showLoadingIndicator: true,
                staticTabs: [
                    {
                        entityId: '1',
                        name: locService.CreateMenuLinkYammerPostTitle,
                        contentUrl:
                            '',
                        websiteUrl:
                            '',
                        scopes: []
                    }
                ],
                version: '1.0.0.' //TODO
            });
        }
        if (capabilities.powerBiCreateEnabled) {
            this.appManifests.set('powerbi', {
                name: locService.PowerBiName,
                creationFileType: FileType[FileType.PowerBI],
                fileTypeNameLocalized: locService.CreateFileTypeNamePowerBiReport,
                id: 'PowerBIAppId',
                showLoadingIndicator: true,
                staticTabs: [
                    {
                        entityId: '1',
                        name: locService.CreateMenuLinkPowerBiTitle,
                        contentUrl:
                            '',
                        websiteUrl:
                            'groups/me/quickcreate',
                        scopes: []
                    }
                ],
                version: '1.0.0.' //TODO
            });
        }
        if (capabilities.whiteboardCreateEnabled) {
            if (!this.whiteboardTenantCheck) {
                const fluidPromise: Promise<boolean> = this.vroomService.getFluidEnabled(FetchStrategy.CacheOnly)
                    .catch(() => this.vroomService.getFluidEnabled(FetchStrategy.NetworkFirst));
                this.whiteboardTenantCheck = withTimeout(5000, fluidPromise);
            }
            this.whiteboardTenantCheck = this.whiteboardTenantCheck.then(fluidEnabled => {
                if (fluidEnabled) {
                    this.appManifests.set('whiteboard', {
                        name: locService.WhiteboardName,
                        creationFileType: FileType[FileType.Whiteboard],
                        fileTypeNameLocalized: locService.CreateFileTypeNameWhiteboardWhiteboards,
                        id: 'WhiteboardAppId',
                        showLoadingIndicator: true,
                        staticTabs: [
                            {
                                entityId: '1',
                                name: locService.CreateMenuLinkWhiteboardTitle,
                                contentUrl:
                                    'openCreate=true',
                                websiteUrl:
                                    '',
                                scopes: []
                            }
                        ],
                        version: '1.0.0.' //TODO
                    });
                }

                return fluidEnabled;
            });
        } else {
            this.whiteboardTenantCheck = Promise.resolve(true);
        }

        if (capabilities.createDesignerEnabled && this.isMsa && this.contextService.sharedContext.officeMarket.toLowerCase() === 'en-us') {
            this.appManifests.set('designer', {
                name: 'Designer',
                creationFileType: FileType[FileType.Designer],
                fileTypeNameLocalized: 'Design', // TODO: Add localization
                id: 'DesignerAppId',
                showLoadingIndicator: true,
                staticTabs: [
                    {
                        entityId: '1',
                        name: 'designer',
                        contentUrl:
                            '',
                        websiteUrl:
                            'https://designer.microsoft.com/new?referrer=harmony',
                        scopes: []
                    }
                ],
                version: '1.0.0.' //TODO
            });
        }
    }

    public getCreateLinks(clickLocation?: string): Promise<Array<CreateLink>> {
        if (!this.createLinksPromise) {
            this.createLinksPromise = new Promise<Array<CreateLink>>(resolve => {
                const createLinks = [];
                this.workloadsService.getProvisionedWorkloads().then((workloads: Array<Workload>) => {
                    workloads = workloads || [];
                    const ordering = this.getMenuOrdering();
                    ordering.forEach(fileType => {
                        let workload = undefined;
                        // Since Fluid is not a separate workload, for FileType.Fluid create a fake workload
                        if (fileType === FileType.Fluid || fileType === FileType.FluidActivityBlueprint) {
                            workload = this.getFluidWorkload(fileType);
                        } else if (fileType === FileType.Trello) {
                            workload = this.getThirdPartyWorkload(fileType);
                        } else {
                            // Both a Forms survey and Forms Quiz use the same workload
                            workload = workloads.find(w => (w.FileType === fileType)
                                || (fileType === FileType.FormsQuiz && w.FileType === FileType.Forms)
                                || (fileType === FileType.OneNotePage && w.FileType === FileType.OneNote)
                                || (fileType === FileType.ScreenRecording && w.FileType === FileType.Stream));
                        }

                        if (workload) {
                            const secondaryFileType: FileType = (fileType === FileType.FormsQuiz ? FileType.FormsQuiz :
                                fileType === FileType.OneNotePage ? FileType.OneNotePage :
                                fileType === FileType.ScreenRecording ? FileType.ScreenRecording : null);
                            const createLink = this.getCreateLinkForWorkload(workload, secondaryFileType, clickLocation);
                            if (createLink) {
                                createLinks.push(createLink);
                            }
                        }
                    });

                    resolve(createLinks);
                });
            }).then(createLinks => {
                const thirdPartyPromises: Array<Promise<CreateLink>> = [];
                CreateDocumentService.thirdPartyOrdering.forEach(fileType => {
                    thirdPartyPromises.push(this.getThirdPartyAppsCreateLink(FileType[fileType].toLowerCase()));
                });
                const firstPartyPromises: Array<Promise<CreateLink>> = [];
                const combineCreateLinks = () => {
                    CreateDocumentService.firstPartyCreateIntentOrdering.forEach(fileType => {
                        firstPartyPromises.push(this.getFirstPartyAppsCreateLink(FileType[fileType].toLowerCase()));
                    });
                    return this.getCreateTilesOrder().then((combinedOrdering : Array<FileType>) =>
                        Promise.all(firstPartyPromises.concat(thirdPartyPromises)).then(manifestCreateLinks => {
                            manifestCreateLinks = manifestCreateLinks.filter(link => !!link);
                            const fullCreateLinks = createLinks.concat(manifestCreateLinks);
                            return fullCreateLinks.sort((linkA, linkB) => combinedOrdering.indexOf(linkA && linkA.workload ? linkA.workload.FileType : FileType.None) - combinedOrdering.indexOf(linkB && linkB.workload ? linkB.workload.FileType : FileType.None));
                        })
                    );
                };

                return this.whiteboardTenantCheck.then(combineCreateLinks, combineCreateLinks);
            });
        }

        return this.createLinksPromise;
    }

    public getDesignerCreateLink(): CreateLink {
        const workload = this.getDesignerWorkload();
        const title = 'Designer Design';
        return {
            ariaLabel: this.locService.format(this.locService.CreateMenuLinkAriaLabelFormat, title),
            title,
            fileTypeName: 'Design',
            appName: 'Designer',
            icon: 'Designer_AppIcon',
            svgButtonBackground: CreateDocumentService.getCreateButtonBackground(FileType.Designer),
            url: UrlBuilder.appendQuery(workload.Url, `cid=${this.clientStartupConfigService.config.sharedClientStartupContext.correlationId}`),
            creationFileType: FileType.Designer,
            getId: () => 'Designer',
            createFromManifest: true,
            workload
        };
    }

    public getListCreateLink(): CreateLink {
        const workload = this.getListsWorkload();
        const title = this.locService.CreateMenuLinkListsTitle || this.locService.ListsName;
        return {
            ariaLabel: this.locService.format(this.locService.CreateMenuLinkAriaLabelFormat, title),
            title,
            fileTypeName: this.locService.CreateFileTypeNameListsList,
            appName: this.locService.ListsName,
            icon: null,
            svgButtonBackground: CreateDocumentService.getCreateButtonBackground(FileType.List),
            url: workload.Url,
            creationFileType: FileType.List,
            getId: () => this.locService.ListsName,
            createFromManifest: true,
            workload: this.getListsWorkload()
        };
    }

    public getCreateLinksForConsumer(): Array<CreateLink> {
        const createLinks = [];
        const createConfig = this.createConfigService.config;

        CreateDocumentService.fileTypeToConsumerWorkloadIdMap.forEach((createLinkId, creationFileType) => {
            if (creationFileType === FileType.List && this.capabilitiesService.capabilities.createOnHomeListsEnabled) {
                const createLink = this.getListCreateLink();
                createLinks.push(createLink);
            } else if (creationFileType === FileType.Designer && this.capabilitiesService.capabilities.createOnHomeDesignerEnabled && this.contextService.sharedContext.officeMarket.toLowerCase() === 'en-us') {
                const createLink = this.getDesignerCreateLink();
                createLinks.push(createLink);
            } else {
                const createCandidate = this.createLinkCandidates.get(creationFileType);
                if (!createCandidate) {
                    return null;
                }

                const url = creationFileType === FileType.Sway ? UrlBuilder.appendQuery(createConfig.createSwayEndpointConsumer, 'campaign=OHPCreateNew') : null;
                const createLink: CreateLink = {
                    ariaLabel: this.locService.format(this.locService.CreateMenuLinkAriaLabelFormat, createCandidate.title),
                    title: createCandidate.title,
                    fileTypeName: createCandidate.fileTypeName,
                    appName: createCandidate.appName,
                    icon: FileItem.documentIconFromType(creationFileType === FileType.FormsQuiz ? FileType.Forms : creationFileType, '32x32'),
                    svgButtonBackground: CreateDocumentService.getCreateButtonBackground(creationFileType),
                    url,
                    creationFileType,
                    getId: () => creationFileType === FileType.FormsQuiz ? createLinkId + 'Quiz' : createLinkId
                };
                createLinks.push(createLink);
            }
        });
        return createLinks;
    }

    public getCreateTilesOrder(): Promise<Array<FileType>> {
        const defaultOrdering = this.getCombinedMenuOrdering();
        if (this.capabilitiesService.capabilities.createDynamicOrderingEnabled) {
            const dynamicOrdering : Array<FileType> = new Array();

            return this.mruDataService.getMruDocuments(new Set(CreateDocumentService.combinedMenuOrdering)).results.pipe(take(1)).toPromise().then(mruData => {
                if (!mruData && !mruData.items && !mruData.items.length) {
                    return defaultOrdering;
                } else {
                    const frequencyMap = new Map<string , number>();
                    mruData.items.forEach(mruItem => {
                        const type = mruItem.FileType;
                        frequencyMap.set(type, frequencyMap.has(type) ? frequencyMap.get(type) + 1 : 1);
                    });

                    const sortedFrequencyMap = new Map([...frequencyMap.entries()].sort((a, b) => b[1] - a[1]));

                    for (const key of sortedFrequencyMap.keys()) {
                        const typeFile : FileType = FileItem.fileTypeNameToFileType(key);
                        dynamicOrdering.push(typeFile);
                    }

                    defaultOrdering.forEach(remainingFileType => {
                        if (!frequencyMap.has(FileType[remainingFileType])) {
                            const typeFile : FileType = FileType[FileType[remainingFileType]];
                            dynamicOrdering.push(typeFile);
                        }
                    });
                }
                return Promise.resolve(dynamicOrdering);
            });
        } else {
            return Promise.resolve(defaultOrdering);
        }
    }

    // Todo: Should have a create Item method for each fileType.
    public createItem(
        app: FileType,
        fallbackUrl?: string,
        template?: Template,
        resultHandler?: CreateResultHandler,
        driveId: string = '',
        createLocation?: FormsCreateLocation,
        linkTarget?: LinkTarget,
        clickLocation?: string,
        skipNavigation = false
    ): Promise<void> {
        // Call the Create space dialog if the app is Spaces.
        // TODO Check to see if we want to add it to the LinkCandidate Map and follow the below codepath (?)
        const scenarioId = this.capabilitiesService.capabilities.scenarioId;
        switch (app) {
            case FileType.Visio:
                return this.workloadsService.getWorkloads().then(workloads => {
                    const hasVisio = !!workloads.find(w => w.FileType === FileType.Visio);
                    const oneDriveWorkload = !!workloads.find(w => w.Id === 'ShellDocuments');

                    if (hasVisio) {
                        return this.createItemHelper(app, fallbackUrl, template, resultHandler, driveId, createLocation, linkTarget, clickLocation, skipNavigation);
                    } else if (scenarioId) {
                        this.shellService.getLicenseStatus(FileType[app], scenarioId).then(licenseStatus => {
                            if (!licenseStatus.IsLicensed) {
                                return this.errorDialogService.showErrorDialog({ category: ErrorDialogErrorCategory.StartPage, error: ErrorDialogErrorType.NoLicenceVisio, fileType: app });
                            }
                            return this.createItemHelper(app, fallbackUrl, template, resultHandler, driveId, createLocation, linkTarget, clickLocation, skipNavigation);
                        });
                    } else if (oneDriveWorkload) {
                        return this.errorDialogService.showErrorDialog({ category: ErrorDialogErrorCategory.StartPage, error: ErrorDialogErrorType.NoLicenceVisio, fileType: app });
                    } else {
                        // If onedrive workload is not present show generic license error.
                        return this.errorDialogService.showErrorDialog({ category: ErrorDialogErrorCategory.StartPage, error: ErrorDialogErrorType.NoLicence, fileType: app });
                    }
                });
            case FileType.Stream:
            case FileType.ScreenRecording:
                this.createStreamService.createStreamContent(
                    this.clientStartupConfigService.config.sharedClientStartupContext.correlationId,
                    app,
                    clickLocation,
                    resultHandler
                );
                return;
            case FileType.PlayLists:
            case FileType.PlayList:
                this.capabilitiesService.capabilities.createStreamPlaylistEnabled &&
                    this.createStreamService.createStreamContent(
                        this.clientStartupConfigService.config.sharedClientStartupContext.correlationId,
                        FileType.PlayList,
                        clickLocation,
                        resultHandler,
                        this.capabilitiesService.capabilities.createPlaylistWithNewUrlEnabled
                    );
                return;
            default:
                return this.createItemHelper(app, fallbackUrl, template, resultHandler, driveId, createLocation, linkTarget, clickLocation, skipNavigation);
        }
    }

    public createItemFromManifest(
        workload: Workload,
        resultHandler: CreateResultHandler
    ): Promise<void> {
        const startTime = Date.now();
        const targetTab = this.isPwa ? undefined : this.windowService.nativeWindow.open('', LinkTarget.NewTab);
        return new Promise<void>(resolve => this.requestNewItemFromManifest(workload.FileType, workload.IsThirdParty ? workload.ThirdPartyCreateUrl : workload.Url, targetTab, resultHandler, startTime, resolve));
    }

    private static getCreateButtonBackground(fileType: FileType): string {
        return  FileType[fileType] + '_ButtonBackground';
    }

    private createItemHelper(
        app: FileType,
        fallbackUrl?: string,
        template?: Template,
        resultHandler?: CreateResultHandler,
        driveId: string = '',
        createLocation?: FormsCreateLocation,
        linkTarget?: LinkTarget,
        clickLocation?: string,
        skipNavigation?: boolean
    ): Promise<void> {
        const candidate = this.createLinkCandidates.get(app);
        if (!candidate) {
            resultHandler && resultHandler({ type: CreateDocumentResultType.Error, latency: 0, errorMsg: CreationErrorType.NotFound, debugInfo: `${FileType[app]} candidate not found` });
            return Promise.resolve();
        }

        const createFileHelper = () => {
            const shouldOpenInUIHost = this.shouldOpenInUIHost(app, driveId);
            return new Promise<boolean | void>(resolve => {
                const defaultLinkTarget = linkTarget || this.getNewDocLinkTarget(app);
                const resolveWrapper = (createResult: CreateDocumentResult) => {
                    resultHandler && resultHandler(createResult);
                    resolve(createResult.type === CreateDocumentResultType.CreatePending);
                };

                // If the new doc is opening in the same tab, then don't try to create multiple documents at the same time.
                // This can happen if the APIs take a long time and the user quickly clicks on multiple template items, or double clicks, etc.
                if ((defaultLinkTarget === LinkTarget.SameFrame || shouldOpenInUIHost) && this.numCreatesPending > 0) {
                    resolveWrapper({ type: CreateDocumentResultType.CreatePending, latency: -1, errorMsg: CreationErrorType.MultipleCreates, debugInfo: `createsPending=${this.numCreatesPending}`});
                    return;
                }

                this.numCreatesPending++;
                if (candidate.canCreateBlankDocument) {
                    const startTime = Date.now();
                    const nativeWindow = this.windowService.nativeWindow;
                    let targetTab: Window;

                    const helper = (filename?: string) => {
                        // PWA TODO: Figure out if it is possible to use the new create doc experience within UWP/PWA context
                        if (candidate.createDocAspxEnabled && !shouldOpenInUIHost) {
                            const apiUrlPromise = this.getDocAspxApiUrl(candidate.docAspxId, this.getOriginParam(true, !!template), startTime, template ? template.AssetId : null, driveId);

                            apiUrlPromise.then(urls => {
                                const tenantUrl = urls[1];
                                const tokenPromise = this.accessTokenService.getAccessToken(AccessTokenConsumer.OneDrive, false, tenantUrl);
                                tokenPromise.then(result => {
                                    const apiUrl = urls[0];
                                    const tokenResponse: AccessTokenResponse = result;

                                    if (!tokenResponse || !tokenResponse.success) {
                                        // TODO show an error message instead of falling back to Graph
                                        // Fallback to Graph flow if this fails.
                                        this.requestNewItem(candidate, app, startTime, fallbackUrl, targetTab, resolve, resultHandler, filename, template, createLocation, null, clickLocation);
                                        return;
                                    }

                                    if (defaultLinkTarget === LinkTarget.NewTab) {
                                        targetTab = nativeWindow.open('', LinkTarget.NewTab);
                                        this.newTabWindow = targetTab;
                                    } else {
                                        targetTab = nativeWindow;
                                    }

                                    let tabDocument: HTMLDocument;
                                    if (targetTab && targetTab !== nativeWindow) {
                                        targetTab.document.write(newTabHtmlText);
                                        tabDocument = targetTab.document;
                                    } else {
                                        // If failed to open new tab then attempt opening inside current tab
                                        tabDocument = this.documentService.nativeDocument;
                                    }

                                    const formHtml = this.getDocaspxFormHtml(apiUrl, tokenResponse.token);
                                    this.documentService.appendHTMLToElement(tabDocument.body, formHtml);
                                    const form = tabDocument.getElementById('create-new-doc-form') as HTMLFormElement;
                                    if (form) {
                                        // Make sure to log before submitting the form just in case the form submit happens in the same tab.
                                        const latency = Date.now() - startTime;
                                        resultHandler && resultHandler({ type: CreateDocumentResultType.GoToBlankDocInstant, latency, platform: KnownReferralPlatform.Web, errorMsg: CreationErrorType.Ok, debugInfo: 'docaspx successful' });
                                        form.submit();
                                        resolve();
                                    } else {
                                        this.requestNewItem(candidate, app, startTime, fallbackUrl, targetTab, resolve, resultHandler, filename, template, createLocation, null, clickLocation, skipNavigation);
                                    }
                                });
                            }).catch(() => {
                                // TODO check error for 404 from Graph root and show an error message instead of falling back to Graph
                                // Fallback to Graph flow if this fails.
                                this.requestNewItem(candidate, app, startTime, fallbackUrl, targetTab, resolve, resultHandler, filename, template, createLocation, null, clickLocation, skipNavigation);
                            });
                        } else {
                            this.requestNewItem(candidate, app, startTime, fallbackUrl, targetTab, resolve, resultHandler, filename, template, createLocation, driveId, clickLocation, skipNavigation);
                        }
                    };

                    const WindowTypes = {
                        // Sets the window that will be used to open the newly created doucment in. Setting this is meaningless if the service later finds that it can be opened inside of the native
                        // Office Win32 App.
                        NativeWindow: () => {
                            targetTab = nativeWindow;
                        },
                        Blank: () => {
                            if (!this.capabilitiesService.capabilities.contentLauncherEnabled) {
                                targetTab = nativeWindow.open('', LinkTarget.NewTab);
                                this.newTabWindow = targetTab;
                            }
                        }
                    };

                    const setWindow =
                        !this.isPwa
                            && !shouldOpenInUIHost
                            && defaultLinkTarget === LinkTarget.NewTab
                            && !(this.fluidSupportEnabled && app === FileType.Fluid)
                            ? WindowTypes.Blank
                            : WindowTypes.NativeWindow;

                    switch (app) {
                        // Decides which FilenameInputDialog to use for specific app. Otherwise, resort to using no filename input.
                        case FileType.OneNote:
                            this.instrumentationService.logClickEvent(
                                KnownInstrumentationEvents.OneNoteNotebookCreateLinkClick,
                                'OneNoteNotebookCreateLink',
                                clickLocation
                            );
                            logUserAction({
                                eventName: KnownInstrumentationEvents.OneNoteNotebookCreateLinkClick,
                                id: 'OneNoteNotebookCreateLink',
                                area: clickLocation ? clickLocation as Area : 'InputDialog',
                                result: 'Open',
                                target: 'Other'
                            });
                            const templateResources: TemplatesResources = this.templatesConfigService.resources(app);
                            this.inputDialogService.showInputDialog(InputDialogFactory.createOnenoteNotebookDialog(this.locService, templateResources, this.oneNoteGraphService, (fileName: string) => {
                                setWindow();
                                helper(fileName);
                            }, () => {
                                this.numCreatesPending--;
                            }));
                            return;
                        case FileType.OneNotePage:
                            this.instrumentationService.logClickEvent(
                                KnownInstrumentationEvents.OneNotePageCreateLinkClick,
                                'OneNotePageCreateLink',
                                clickLocation
                            );
                            logUserAction({
                                eventName: KnownInstrumentationEvents.OneNotePageCreateLinkClick,
                                id: 'OneNotePageCreateLink',
                                area: clickLocation ? clickLocation as Area : 'InputDialog',
                                result: 'Open',
                                target: 'Other'
                            });
                            this.mruDataService.getMruDocuments(new Set([FileType.OneNote])).results.pipe(take(1)).toPromise().then(oneNoteResult => {
                                this.createOneNotePageDialogService.showOneNotePageDialog({
                                    notebookItems: oneNoteResult && oneNoteResult.items || [],
                                    onClick: () => resultHandler({type: CreateDocumentResultType.GoToBlankDoc, latency: 0})
                                });
                            });
                            return;
                        default:
                            setWindow();
                            helper();
                    }
                } else {
                    const fallbackToLandingPage = () => {
                        if (fallbackUrl && candidate.canFallbackToLandingPage) {
                            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                                import('@office-start/launcher-service').then(
                                    launchSvc => {
                                        launchSvc.launchUrl(fallbackUrl, {action: 'CreateNewFallback'}).then(() => {
                                            resolveWrapper({ type: CreateDocumentResultType.GoToLanding, latency: 0, platform: KnownReferralPlatform.Web, errorMsg: CreationErrorType.Ok, debugInfo: 'fallback to landing page' });
                                        }).catch(() => {
                                            this.showError(ErrorDialogErrorType.Generic, candidate.fileType, ErrorDialogErrorCategory.OpenFailed, fallbackUrl);
                                            resolveWrapper({ type: CreateDocumentResultType.Error, latency: 0, errorMsg: CreationErrorType.LaunchFailed, debugInfo: 'failed to launch fallback to landing page' });
                                        });
                                    }
                                );
                                return;
                            } else {
                                this.urlLauncherService.openUrl(fallbackUrl, LaunchAction.CreateNewFallback).then(() => {
                                    resolveWrapper({ type: CreateDocumentResultType.GoToLanding, latency: 0, platform: KnownReferralPlatform.Web, errorMsg: CreationErrorType.Ok, debugInfo: 'fallback to landing page' });
                                }).catch(() => {
                                    this.showError(ErrorDialogErrorType.Generic, candidate.fileType, ErrorDialogErrorCategory.OpenFailed, fallbackUrl);
                                    resolveWrapper({ type: CreateDocumentResultType.Error, latency: 0, errorMsg: CreationErrorType.LaunchFailed, debugInfo: 'failed to launch fallback to landing page, contentLauncherNotEnabled' });
                                });
                                return;
                            }
                        }

                        this.showError(ErrorDialogErrorType.Generic, candidate.fileType);
                        const errorMsg = !candidate.canFallbackToLandingPage ? CreationErrorType.Disabled : !fallbackUrl ? CreationErrorType.NotFound : CreationErrorType.LaunchFailed;
                        const debugInfo = !candidate.canFallbackToLandingPage ? 'fallback disabled' : !fallbackUrl ? 'fallback url undefined' : 'launch office app failed';
                        resolveWrapper({ type: CreateDocumentResultType.Error, latency: 0, errorMsg, debugInfo});
                    };

                    // Try to open native app and fall back to web url
                    this.launchOfficeApp(candidate.fileType).then(() => {
                        resolveWrapper({ type: CreateDocumentResultType.GoToLanding, latency: 0, platform: KnownReferralPlatform.Native, errorMsg: CreationErrorType.Ok, debugInfo: 'launch office app successful' });
                    }).catch(() => {
                        fallbackToLandingPage();
                    });
                }
            }).then((createPending: boolean) => {
                if (!createPending) {
                    this.numCreatesPending--;
                }
            });
        };

        // When users click create new workbook on PWA start page, we don't create new file since Excel or Visio desktop app cannot open 0-byte files.
        // TODO: remove this logic when Excel or Visio win32 supports 0-byte file creation.
        const appsNotSupporting0ByteFile: Array<FileType> = [FileType.Excel, FileType.Visio];
        if (appsNotSupporting0ByteFile.includes(app) && !template && this.capabilitiesService.capabilities.pwaEnabled) {
            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                return import('@office-start/launcher-service').then(
                    launchSvc =>
                        launchSvc.launchApp({ action: 'OpenDocument', event, appName: FileType[app], url: ''})
                            .then(result => {
                                if (result.success) {
                                resultHandler && resultHandler({ type: CreateDocumentResultType.GoToLanding, latency: 0, platform: KnownReferralPlatform.Native, errorMsg: CreationErrorType.Ok, debugInfo: 'launch app not supporting 0-byte successful' });
                                return Promise.resolve();
                            } else {
                                return createFileHelper();
                            }
                        }).catch(createFileHelper)
                );
            } else {
                return this.localAppLauncherService.launchOfficeApp(FileType[app], '', null, true).then(success => {
                    if (success) {
                        resultHandler && resultHandler({ type: CreateDocumentResultType.GoToLanding, latency: 0, platform: KnownReferralPlatform.Native, errorMsg: CreationErrorType.Ok, debugInfo: 'launch app not supporting 0-byte successful' });
                        return Promise.resolve();
                    } else {
                        return createFileHelper();
                    }
                }).catch(createFileHelper);
            }
        } else {
            return createFileHelper();
        }
    }

    private getFirstPartyAppsCreateLink(app: string): Promise<CreateLink> {
        return new Promise(resolve => {
            const appManifest = this.appManifests.get(app);
            if (appManifest) {
                this.getFirstPartyGenericWorkload(appManifest).then(workload =>
                    resolve(workload ? this.getCreateLinkForFirstPartyWorkload(workload) : null)
                ).catch(() => resolve(null));
            } else {
                resolve(null);
            }
        });
    }

    private getThirdPartyAppsCreateLink(app: string): Promise<CreateLink> {
        return new Promise(resolve => {
            const appManifest = this.appManifests.get(app);
            if (appManifest) {
                const workload = this.getThirdPartyGenericWorkload(appManifest);
                resolve(workload ? this.getCreateLinkForThirdPartyWorkload(workload) : null);
            } else {
                resolve(null);
            }
        });
    }

    private getFirstPartyGenericWorkload(appManifest: AppManifestCreateIntent): Promise<Workload> {
        return this.workloadsService.getWorkloads().then(workloads => {
            let firstPartyWorkload = null;
            if (appManifest.creationFileType === FileType[FileType.Designer]) { // TODO: Remove check when Designer is onboarded to shell
                firstPartyWorkload = this.getDesignerWorkload();
            } else {
                firstPartyWorkload = workloads.find(workload => workload?.AppId === appManifest.name || workload.AppId?.includes(appManifest.name.replace(/\s/g, ''))); // TODO: This is due to PowerBI being PowerBIv2 from shell
            }

            if (!firstPartyWorkload) {
                return null;
            }

            let url;
            if (appManifest.creationFileType === FileType[FileType.Designer]) { // TODO: Remove check when Designer is onboarded to shell
                url = firstPartyWorkload.Url + `&cid=${this.clientStartupConfigService.config.sharedClientStartupContext.correlationId}`;
            } else {
                const shellUrlItems = firstPartyWorkload.Url?.split('?');
                const modifiedShellUrl = `${shellUrlItems[0]}${shellUrlItems[0].endsWith('/') ? '' : '/'}${appManifest.staticTabs && appManifest.staticTabs[0] && appManifest.staticTabs[0]['websiteUrl'] ? appManifest.staticTabs[0]['websiteUrl'] : ''}`;
                const modifiedShellParams = `${shellUrlItems[1] ? shellUrlItems[1] + '&' : ''}${appManifest.staticTabs && appManifest.staticTabs[0] && appManifest.staticTabs[0]['contentUrl'] ? appManifest.staticTabs[0]['contentUrl'] + '&' : ''}cid=${this.clientStartupConfigService.config.sharedClientStartupContext.correlationId}`;
                url = `${modifiedShellUrl}?${modifiedShellParams}`;
            }

            return {
                getId: () => appManifest.name.replace(/\s/g, ''), // For Power BI
                Id: appManifest.name.replace(/\s/g, ''),
                Referral: appManifest.name.replace(/\s/g, ''),
                FileType: !!appManifest.creationFileType ? FileItem.fileTypeNameToFileType(appManifest.creationFileType) : undefined,
                FileTypeNameLocalized: appManifest.fileTypeNameLocalized,
                Url: url,
                Color: firstPartyWorkload.Color,
                IconUnicodeCodepointCssClass: firstPartyWorkload.IconUnicodeCodepointCssClass,
                Text: appManifest.name,
                SubText: undefined,
                Title: appManifest.staticTabs && appManifest.staticTabs[0].name || appManifest.name,
                IsThirdParty: false,
                IsNativeClientOnlyApp: false,
                IconFont: undefined,
                Size: undefined,
                ClickCount: undefined,
                FirstOccurrence: undefined,
                LastOccurrence: undefined,
                InAppNav: undefined,
                ThirdPartyIcon: undefined,
                HideTile: undefined,
                ChangeColorInDarkMode: undefined,
                AppId: appManifest.id,
                CollectorId: undefined,
                ThirdPartyCreateUrl: ''
            };
        });
    }

    private getThirdPartyGenericWorkload(appManifest: IM365AppDefinition): Workload {
        return {
            getId: () => appManifest.name,
            Id: appManifest.name,
            Referral: appManifest.name,
            FileType: FileType.ThirdParty, // Do we need FileType for every app? I don't think so?
            Url: '',
            Color: appManifest.accentColor,
            IconUnicodeCodepointCssClass: '',
            Text: appManifest.name,
            SubText: undefined,
            Title: appManifest.name,
            IsThirdParty: true,
            IsNativeClientOnlyApp: false,
            IconFont: undefined,
            Size: undefined,
            ClickCount: undefined,
            FirstOccurrence: undefined,
            LastOccurrence: undefined,
            InAppNav: undefined,
            ThirdPartyIcon: appManifest.largeImageUrl && appManifest.largeImageUrl !== '' ? appManifest.largeImageUrl : appManifest.smallImageUrl,
            HideTile: undefined,
            ChangeColorInDarkMode: undefined,
            AppId: appManifest.id,
            CollectorId: undefined,
            ThirdPartyCreateUrl: appManifest.staticTabs && appManifest.staticTabs[0] && appManifest.staticTabs[0]['contentUrl']
        };
    }

    private getThirdPartyWorkload(fileType: FileType): Workload {
        if (fileType === FileType.Trello) {
            return {
                getId: () => 'ShellTrello',
                Id: 'ShellTrello',
                Referral: 'Trello',
                FileType: FileType.Trello,
                Url: '',
                Color: '#008FE4',
                IconUnicodeCodepointCssClass: 'ms-ohp-svg-Icon ms-ohp-Icon ms-ohp-Icon--trelloLogo',
                Text: undefined,
                SubText: undefined,
                Title: undefined,
                IsThirdParty: false,
                IsNativeClientOnlyApp: false,
                IconFont: undefined,
                Size: undefined,
                ClickCount: undefined,
                FirstOccurrence: undefined,
                LastOccurrence: undefined,
                InAppNav: undefined,
                ThirdPartyIcon: undefined,
                HideTile: undefined,
                ChangeColorInDarkMode: undefined,
                AppId: undefined,
                CollectorId: undefined,
                ThirdPartyCreateUrl: undefined
            };
        } else {
            return null;
        }
    }

    // Remove after migration
    private getListsWorkload(): Workload {
        return {
            getId: () => 'Lists',
            Color: '#974B8B',
            IconUnicodeCodepointCssClass: '',
            IconFont: undefined,
            Id: 'Lists',
            Referral: 'Lists',
            Size: undefined,
            Text: this.locService.ListsName,
            SubText: undefined,
            Title: this.locService.CreateMenuLinkListsTitle || this.locService.ListsName,
            Url: UrlBuilder.appendQuery('https://lists.live.com/?', `openCreate=true&workload=Office&ocid=OfficeConsumerWebCreate&cid=${this.clientStartupConfigService.config.sharedClientStartupContext.correlationId}`),
            ClickCount: undefined,
            FirstOccurrence: undefined,
            LastOccurrence: undefined,
            InAppNav: undefined,
            FileType: FileType.List,
            FileTypeNameLocalized: undefined,
            ThirdPartyIcon: undefined,
            IsThirdParty: false,
            IsNativeClientOnlyApp: false,
            HideTile: undefined,
            ChangeColorInDarkMode: undefined,
            AppId: undefined,
            CollectorId: undefined,
            ThirdPartyCreateUrl: undefined
        };
    }

    // TODO: Remove once Designer is onboarded
    private getDesignerWorkload(): Workload {
        return {
            getId: () => 'DesignerAppId',
            Color: '#593EF2',
            IconUnicodeCodepointCssClass: '',
            IconFont: undefined,
            Id: 'DesignerAppId',
            Referral: 'Designer',
            Size: undefined,
            Text: undefined,
            SubText: undefined,
            Title: undefined,
            Url: 'https://designer.microsoft.com/new?referrer=harmony',
            ClickCount: undefined,
            FirstOccurrence: undefined,
            LastOccurrence: undefined,
            InAppNav: undefined,
            FileType: FileType.Designer,
            FileTypeNameLocalized: undefined,
            ThirdPartyIcon: undefined,
            IsThirdParty: false,
            IsNativeClientOnlyApp: false,
            HideTile: undefined,
            ChangeColorInDarkMode: undefined,
            AppId: undefined,
            CollectorId: undefined,
            ThirdPartyCreateUrl: undefined
        };
    }

    private getFluidWorkload(fileType: FileType): Workload {
        const fluidWorkload = {
            getId: () => 'ShellFluid',
            Id: 'ShellFluid',
            Referral: 'Fluid',
            FileType: FileType.Fluid,
            Url: '',
            Color: '#0078D4',
            IconUnicodeCodepointCssClass: 'ms-ohp-svg-Icon ms-ohp-Icon ms-ohp-Icon--fluidLogo',
            Text: undefined,
            SubText: undefined,
            Title: undefined,
            IsThirdParty: false,
            IsNativeClientOnlyApp: false,
            IconFont: undefined,
            Size: undefined,
            ClickCount: undefined,
            FirstOccurrence: undefined,
            LastOccurrence: undefined,
            InAppNav: undefined,
            ThirdPartyIcon: undefined,
            HideTile: undefined,
            ChangeColorInDarkMode: undefined,
            AppId: undefined,
            CollectorId: undefined,
            ThirdPartyCreateUrl: undefined
        };

        switch (fileType) {
            case FileType.Fluid:
                return fluidWorkload;
            case FileType.FluidActivityBlueprint:
                fluidWorkload.getId = () => 'ShellFluidAbp';
                fluidWorkload.Id = 'ShellFluidAbp';
                fluidWorkload.FileType = FileType.FluidActivityBlueprint;
                fluidWorkload.Url = '';
                return fluidWorkload;
            default:
                break;
        }
    }

    private requestNewItem(
        candidate: CreateLinkCandidate,
        app: FileType,
        clickTime: number,
        fallbackUrl: string,
        targetTab: Window,
        resolve: () => void,
        resultHandler: CreateResultHandler,
        fileName?: string,
        template?: Template,
        createLocation?: FormsCreateLocation,
        templateDriveId?: string,
        clickLocation?: string,
        skipNavigation?: boolean
    ) {
        const resolveWrapper = (result: CreateDocumentResult) => {
            resultHandler && resultHandler(result);
            resolve();
        };

        const getLatency = () => Date.now() - clickTime;
        const openUrl: (url: string, successResultType: CreateDocumentResultType, launchAction: LaunchAction) => void = (url, successResultType, launchAction) => {
            const openError = (category: ErrorDialogErrorCategory, errorMsg: CreationErrorType, debugInfo: string) => {
                this.showError(
                    category === ErrorDialogErrorCategory.OpenFailed && launchAction === LaunchAction.GoToBlankDoc ? ErrorDialogErrorType.OpenNewDocFailed : ErrorDialogErrorType.Generic,
                    candidate.fileType,
                    category,
                    category === ErrorDialogErrorCategory.OpenFailed ? url : ''
                );
                resolveWrapper({ type: CreateDocumentResultType.Error, latency: getLatency(), errorMsg, debugInfo: debugInfo ?? '' });
            };

            if (!url) {
                openError(ErrorDialogErrorCategory.CreateFile, CreationErrorType.NotFound, 'url not found');
                return;
            }

            if (this.isPwa) {
                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                    import('@office-start/launcher-service').then(
                        launchSvc => {
                            launchSvc.launchUrl(url, {action: 'OpenDocument'}).then(() => {
                                // In the PWA a new window is opened to launch the URL, so we can wait for the promise to resolve before logging the result.
                                resolveWrapper({ type: successResultType, latency: getLatency(), platform: KnownReferralPlatform.Web, errorMsg: CreationErrorType.Ok,  debugInfo: 'launch url successful' });
                            }, () => {
                                openError(ErrorDialogErrorCategory.OpenFailed, CreationErrorType.LaunchFailed, 'launchUrl failed');
                            });
                        }
                    );
                    return;
                } else {
                    this.urlLauncherService.openUrl(url, launchAction).then(() => {
                        // In the PWA a new window is opened to launch the URL, so we can wait for the promise to resolve before logging the result.
                        resolveWrapper({ type: successResultType, latency: getLatency(), platform: KnownReferralPlatform.Web, errorMsg: CreationErrorType.Ok, debugInfo: 'launch url successful' });
                    }, () => {
                        openError(ErrorDialogErrorCategory.OpenFailed, CreationErrorType.LaunchFailed, 'launchUrl failed, contentLauncherNotEnabled');
                    });
                    return;
                }
            } else if (typeof targetTab.open !== 'undefined') {
                // Log the result before opening the URL to make sure it gets logged in the case where we open in the same tab.
                resultHandler && resultHandler({ type: successResultType, latency: getLatency(), platform: KnownReferralPlatform.Web, errorMsg: CreationErrorType.Ok, debugInfo: 'launch url successful' });
                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                    import('@office-start/launcher-service').then(
                        launchSvc => {
                            launchSvc.launchUrl(url, {action: 'OpenDocument', linkTarget: '_top', targetTab});
                        }
                    );
                } else {
                    targetTab.open(url, LinkTarget.TopFrame);
                }
                resolve();
                return;
            }

            openError(ErrorDialogErrorCategory.OpenFailed, CreationErrorType.Generic, `openUrl failed, targetTab.open is ${targetTab.open}`);
        };

        // Fall back to opening the workload's main page if there was an error
        const onErrorFallback = (dialogType: ErrorDialogErrorType, errorMsg: CreationErrorType, debugInfo: string, errorCode?: string) => {
            const fallbackToLandingPage = () => {
                if (!fallbackUrl || !candidate.canFallbackToLandingPage) {
                    this.showError(dialogType, candidate.fileType);
                    const latency = getLatency();
                    const error = candidate.canFallbackToLandingPage && !fallbackUrl ? CreationErrorType.NotFound : !candidate.canFallbackToLandingPage ? errorMsg : CreationErrorType.LaunchFailed;
                    const debug = candidate.canFallbackToLandingPage && !fallbackUrl ? 'fallback url undefined' : !candidate.canFallbackToLandingPage ? `fallback disabled, ${debugInfo}` : 'launch office app failed';
                    resolveWrapper({ type: CreateDocumentResultType.Error, latency, errorMsg: error, debugInfo: debug, httpCode: errorCode ?? '' });
                    return;
                }

                openUrl(fallbackUrl, CreateDocumentResultType.GoToLandingFallback, LaunchAction.CreateNewFallback);
            };

            // Try to open native app and fall back to web url
            this.launchOfficeApp(candidate.fileType, '', targetTab).then(() => {
                resolveWrapper({ type: CreateDocumentResultType.GoToLandingFallback, latency: getLatency(), platform: KnownReferralPlatform.Native, errorMsg: CreationErrorType.Ok, debugInfo: 'launch office app successful' });
            }).catch(() => {
                fallbackToLandingPage();
            });
        };

        const createNewFile = () => {
            let createNewPromise: Promise<FileUrlModel> = null;
            let newFileName: string = null;
            let launchAction = LaunchAction.GoToBlankDoc;
            switch (app) {
                case FileType.Sway:
                    createNewPromise = this.swayService.createNew().then(url => ({ canonicalUrl: url, webUrl: url } as FileUrlModel));
                    break;
                case FileType.OneNote:
                    createNewPromise = this.oneNoteGraphService.createNewNotebook(fileName).then(res => {
                        const url = this.addLoginHint(res.links.oneNoteWebUrl.href);
                        return { canonicalUrl: url, webUrl: url } as FileUrlModel;
                    });
                    break;
                case FileType.Forms:
                    if (template) {
                        createNewPromise = this.formsService.createFormFromTemplate(template, FormType.Users).then(url => ({ canonicalUrl: url, webUrl: url }) as FileUrlModel);
                    } else {
                        createNewPromise = this.formsService.createForm(FileType.Forms, createLocation, clickLocation).then(url => ({ canonicalUrl: url, webUrl: url }) as FileUrlModel);
                    }
                    launchAction = LaunchAction.CreateNewFallback;
                    break;
                case FileType.FormsQuiz:
                    createNewPromise = this.formsService.createForm(FileType.FormsQuiz, createLocation, clickLocation).then(url => ({ canonicalUrl: url, webUrl: url }) as FileUrlModel);
                    launchAction = LaunchAction.CreateNewFallback;
                    break;
                default:
                    [newFileName, createNewPromise] = this.createOneDriveFile(candidate, template ? template.AssetId : null, clickTime);
            }

            const bootWithUiHost = this.shouldOpenInUIHost(app, templateDriveId)
                ? this.wacContentService.bootstrapZeroByteFile({ fileName: newFileName, fileType: app, wdOrigin: this.getOriginParam(false, !!template) }, clickTime, createNewPromise, template?.AssetId, templateDriveId)
                : undefined;

            // Flag to stop the document from booting twice
            let documentBooted = false;

            if (!this.capabilitiesService.capabilities.contentLauncherEnabled) {
            // If the UI host is enabled we can attempt to route to the content page
            // before the graph call completes
                if (bootWithUiHost) {
                    if (candidate.canOpenBlankDocInNativeApp) {
                        this.localAppLauncherService.shouldOpenInOfficeApp(FileType[candidate.fileType]).then(openInOfficeApp => {
                            // If there isn't a native app installed we can proceed with the ui host booting.
                            if (!openInOfficeApp && !documentBooted) {
                                documentBooted = true;
                                resultHandler && resultHandler({ type: CreateDocumentResultType.GoToBlankDoc, latency: getLatency(), platform: KnownReferralPlatform.Web, errorMsg: CreationErrorType.Ok, debugInfo: 'route to content page successful' });
                                bootWithUiHost();
                                resolve();
                            }
                        });
                    } else {
                        documentBooted = true;
                        resultHandler && resultHandler({
                            type: CreateDocumentResultType.GoToBlankDoc,
                            latency: getLatency(),
                            platform: KnownReferralPlatform.Web,
                            documentUrlPromise: createNewPromise.then(fileUrlModel => fileUrlModel.canonicalUrl),
                            errorMsg: CreationErrorType.Ok,
                            debugInfo: 'route to content page successful'
                        });
                        if (!skipNavigation) {
                            bootWithUiHost();
                        }
                        resolve();
                    }
                }
            }

            createNewPromise.then(fileUrlModel => {
                if (!documentBooted) {
                    documentBooted = true;
                    if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                        import('@office-start/launcher-service').then(
                            launchSvc => {
                                const document: DocumentReact = {
                                    canonicalUrl: fileUrlModel.canonicalUrl,
                                    webUrl: fileUrlModel.webUrl,
                                    app: FileType[candidate.fileType],
                                    attribution: '',
                                    url: fileUrlModel.canonicalUrl,
                                    extension: '',
                                    title: candidate.appName,
                                    id: fileUrlModel.docId || '',
                                    isRedeemed: true
                                };
                                launchSvc.launchContent({
                                    action: 'OpenDocument',
                                    document,
                                    useWebUrl: true,
                                    wdOriginType: WdOriginTypeReact.New
                                })
                                .then(result => {
                                    if (result.success) {
                                        resolveWrapper({ type: CreateDocumentResultType.GoToBlankDoc, latency: getLatency(), platform: KnownReferralPlatform.Native, errorMsg: CreationErrorType.Ok, debugInfo: 'launch content successful' });
                                        return;
                                    }
                                    this.launchWebApp(fileUrlModel, launchAction, openUrl, resultHandler, resolve, getLatency, bootWithUiHost);
                                })
                                .catch(() => {
                                    this.launchWebApp(fileUrlModel, launchAction, openUrl, resultHandler, resolve, getLatency, bootWithUiHost);
                                });
                            }
                        );
                    } else {
                        if (!candidate.canOpenBlankDocInNativeApp) {
                            // Excel desktop app cannot open 0-byte files. Unknown if Sway/Visio can.
                            // Remove from excel candidate when native app supports 0-byte create flow.
                            // Also remove similar code in other files (search '0-byte' in the code):
                            //     line 651 of pwa-root.component,
                            //     line 36 of pwa-local-app-launcher.service,
                            //     line 238 of create-menu.component,
                            //     line 330 of create-document.service.
                            this.launchWebApp(fileUrlModel, launchAction, openUrl, resultHandler, resolve, getLatency, bootWithUiHost);
                            return;
                        } else {
                            this.launchOfficeApp(candidate.fileType, fileUrlModel.canonicalUrl, this.isPwa ? this.windowService.nativeWindow : targetTab).then(() => {
                                resolveWrapper({ type: CreateDocumentResultType.GoToBlankDoc, latency: getLatency(), platform: KnownReferralPlatform.Native, errorMsg: CreationErrorType.Ok, debugInfo: 'launch office app successful' });
                            }).catch(() => {
                                this.launchWebApp(fileUrlModel, launchAction, openUrl, resultHandler, resolve, getLatency, bootWithUiHost);
                            });
                        }
                    }
                }
            })
                .catch(errorMsg => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(errorMsg, ErrorId.foSs8);

                    const graphError = errorMsg as GraphError;
                    const code = graphError && graphError.error && graphError.error.code || '';
                    if (code === '404') {
                        onErrorFallback(ErrorDialogErrorType.OneDriveProvisioning, CreationErrorType.GraphError, 'OneDriveProvisioning Error', code);
                    } else if (errorMsg && errorMsg.error && errorMsg.error.data === 'CAP_Error') {
                        if (this.isMsa) {
                            onErrorFallback(ErrorDialogErrorType.Generic, CreationErrorType.GraphError, 'Graph CAP_Error', code);
                        } else {
                            onErrorFallback(ErrorDialogErrorType.ConditionalAccessFallback, CreationErrorType.GraphError, 'Graph CAP_Error', code);
                        }
                    } else if (this.isMsa && code === '403') {
                        onErrorFallback(ErrorDialogErrorType.OneDriveDisabled, CreationErrorType.GraphError, 'OneDriveDisabled', code);
                    } else if (!!code) {
                        onErrorFallback(ErrorDialogErrorType.Generic, CreationErrorType.GraphError, 'Other graph error', code);
                    } else {
                        onErrorFallback(ErrorDialogErrorType.Generic,  CreationErrorType.Generic, 'Generic error');
                    }
                });
        };

        // If failed to open new tab then attempt opening inside current tab
        if (!targetTab) {
            targetTab = this.windowService.nativeWindow;
        }

        // On rare occasions we have seen error thrown indicating that open() function is undefined.
        if (!this.isPwa && typeof targetTab.open === 'undefined') {
            onErrorFallback(ErrorDialogErrorType.Generic, CreationErrorType.NotFound, 'open() is undefined');
            return;
        }

        // Only set the loading HTML for new tabs.
        // It's not possible to modify the current window's HTML in this way for same-tab navigation
        if (targetTab !== this.windowService.nativeWindow) {
            try {
                const loadingHtml = this.getLoadingPageHtml(app);
                targetTab.document.write(loadingHtml);
            } catch (exception) {
                // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                this.globalErrorHandler.handleError(exception, ErrorId.T3UKJ);

                onErrorFallback(ErrorDialogErrorType.Generic, CreationErrorType.Generic, 'failed to write loading html');
                return;
            }
        }

        if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
            import('@office-start/launcher-service').then(
                launchSvc => {
                    launchSvc.launchTemplate(FileType[app], template ? template.TemplateBinaryUrl : null).then(success => {
                        if (success) {
                            resolveWrapper({ type: CreateDocumentResultType.GoToBlankDoc, latency: getLatency(), platform: KnownReferralPlatform.Native, errorMsg: CreationErrorType.Ok, debugInfo: 'launchOfficeAppWithTemplate successful' });
                        } else {
                            throw new Error('launchOfficeAppWithTemplate failed');
                        }
                    }).catch(() => {
                        if (app === FileType.Fluid || app === FileType.FluidActivityBlueprint) {
                            // Since we are not actually making a request to create a file, there is no pending state
                            this.numCreatesPending--;
                            this.launchFluidExperience(app);
                        } else {
                            createNewFile();
                        }
                    });
                }
            );
        } else {
            // When create new files with template in PWA, we will try use protocol handler to open the file with template url provided.
            // This does not require a new file being created first.
            this.localAppLauncherService.launchOfficeAppWithTemplate(FileType[app], template ? template.TemplateBinaryUrl : null).then(success => {
                if (success) {
                    resolveWrapper({ type: CreateDocumentResultType.GoToBlankDoc, latency: getLatency(), platform: KnownReferralPlatform.Native, errorMsg: CreationErrorType.Ok, debugInfo: 'launchOfficeAppWithTemplate successful' });
                } else {
                    throw new Error('launchOfficeAppWithTemplate failed');
                }
            }).catch(() => {
                if (app === FileType.Fluid || app === FileType.FluidActivityBlueprint) {
                    // Since we are not actually making a request to create a file, there is no pending state
                    this.numCreatesPending--;
                    this.launchFluidExperience(app);
                } else {
                    createNewFile();
                }
            });
        }
    }

    private requestNewItemFromManifest(appFileType: FileType, createUrl: string, targetTab: Window, resultHandler: CreateResultHandler, clickTime: number, resolve) {
        const resolveWrapper = (result: CreateDocumentResult) => {
            resultHandler && resultHandler(result);
            resolve();
        };
        const getLatency = () => Date.now() - clickTime;
        const openUrl: (url: string, successResultType: CreateDocumentResultType, launchAction: LaunchAction) => void = (url, successResultType, launchAction) => {
            const openError = (category: ErrorDialogErrorCategory, errorMsg: CreationErrorType, debugInfo: string) => {
                this.showError(
                    ErrorDialogErrorType.OpenNewDocFailed,
                    appFileType,
                    category,
                    category === ErrorDialogErrorCategory.OpenFailed ? url : ''
                );
                resolveWrapper({ type: CreateDocumentResultType.Error, latency: getLatency(), errorMsg, debugInfo });
            };
            if (this.isPwa) {
                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                    return import('@office-start/launcher-service').then(
                        launchSvc => launchSvc.launchUrl(url, {action: launchAction.toString() as LaunchActionReact}).then(() => {
                            resolveWrapper({ type: successResultType, latency: getLatency(), platform: KnownReferralPlatform.Web, errorMsg: CreationErrorType.Ok, debugInfo: 'launch url successful' });
                        }, () => {
                            openError(ErrorDialogErrorCategory.OpenFailed, CreationErrorType.LaunchFailed, 'failed to launch url');
                        })
                    );
                } else {
                    return this.urlLauncherService.openUrl(url, launchAction).then(() => {
                        resolveWrapper({ type: successResultType, latency: getLatency(), platform: KnownReferralPlatform.Web });
                    }, () => {
                        openError(ErrorDialogErrorCategory.OpenFailed, CreationErrorType.LaunchFailed, 'failed to launch url, contentLauncherNotEnabled');
                    });
                }
            } else {
                resultHandler && resultHandler({ type: successResultType, latency: getLatency(), platform: KnownReferralPlatform.Web });
                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                    return import('@office-start/launcher-service').then(
                        launchSvc => launchSvc.launchUrl(url, {action: launchAction.toString() as LaunchActionReact, linkTarget: '_top', targetTab})
                    );
                } else {
                    targetTab.open(url, LinkTarget.TopFrame);
                }
                resolve();
            }
        };
        this.launchWebApp({ canonicalUrl: createUrl, webUrl: createUrl } as FileUrlModel, LaunchAction.OpenFile, openUrl, resultHandler, resolve, () => Date.now() - clickTime);
    }

    private getHarmonySessionSource(): string {
        const workload = this.capabilitiesService.getWorkload();
        switch (workload) {
            case WorkloadQueryParameter.Pwa:
                return 'HarmonyDesktop';
            case WorkloadQueryParameter.OfficeHome:
            case WorkloadQueryParameter.OfficeHomeReact:
                return 'HarmonyWeb';
            case WorkloadQueryParameter.Embed:
                return 'HarmonyEmbed';
            default:
                return 'unknown';
        }
    }

    private createOneDriveFile(candidate: CreateLinkCandidate, templateId: string, clickTime: number): [string, Promise<FileUrlModel>] {
        const newFileName = `${candidate.newDocName}.${candidate.fileExtension}`;
        const shouldOpenInUIHost = this.shouldOpenInUIHost(candidate.fileType);
        return [newFileName, new Promise<FileUrlModel>((resolve, reject) => {
            const startTime = Date.now();
            this.oneDriveService.createNewFile(newFileName, candidate.fileType, shouldOpenInUIHost)
                .then(newFile => {
                    const fileUrlModel = {} as FileUrlModel;

                    if (newFile.webDavUrl) {
                        fileUrlModel.canonicalUrl = this.addLoginHint(newFile.webDavUrl);
                    }

                    let webUrl = this.addLoginHint(newFile.webUrl);

                    // PWA TODO: Canonical URLs do not support the actions below. This needs to be revisited once they are enabled.
                    // 'action=editnew' is needed to trigger the WAC apps to convert the empty file to a valid blank document
                    const editNewQueryParam = 'action=editnew';
                    webUrl = webUrl.replace(new RegExp('([\?&])action[^&]*'), '$1' + editNewQueryParam);
                    if (webUrl.lastIndexOf(editNewQueryParam) === -1) {
                        webUrl = UrlBuilder.appendQuery(webUrl, editNewQueryParam);
                    }

                    if (templateId) {
                        webUrl = webUrl + `&wdTpl=${templateId}`;
                    }

                    if (templateId || this.createLcidEnabled) {
                        webUrl += `&wdlcid=${this.contextService.sharedContext.officeMarketLcid}`;
                    }

                    const scenarioId = this.capabilitiesService.capabilities.scenarioId;
                    if (scenarioId) {
                        webUrl += `&wdscenarioid=${scenarioId}`;
                    }

                    // 'wdNewAndOpenCt' is the time the user clicked on the menu item. This is used by the WAC apps
                    // to track the overall time it takes to land a user on a new document. In our case it will help
                    // track the time for the Graph API call along with the redirect to the WAC.
                    const origin = this.getOriginParam(false, !!templateId);
                    webUrl = UrlBuilder.appendQuery(webUrl,
                        `wdNewAndOpenCt=${clickTime}&ct=${clickTime}&wdPreviousSession=${this.startupConfigService.config.sharedClientStartupContext.sessionId}&wdPreviousSessionSrc=${this.getHarmonySessionSource()}&wdOrigin=${origin}`);
                    fileUrlModel.webUrl = webUrl;

                    if (shouldOpenInUIHost) {
                        if (newFile.parentReference) {
                            fileUrlModel.docId = newFile.id;
                            fileUrlModel.driveId = newFile.parentReference.driveId;
                            fileUrlModel.fileName = newFile.name;
                        }

                        fileUrlModel.wopiFetchData = {
                            fetchDiagnostics: { startTime },
                            wopiHostData: VroomService.getWopiHostDataFromItem(newFile)
                        };
                    }

                    resolve(fileUrlModel);
                })
                .catch(error => {
                    // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                    this.globalErrorHandler.handleError(error, ErrorId.dYEKI);

                    reject(error || 'Creating new file failed');
                });
        })];
    }

    private getNewDocLinkTarget(app: FileType): LinkTarget {
        const candidate = this.createLinkCandidates.get(app);
        if (this.capabilitiesService.capabilities.capPopupEnabled &&
            (app === FileType.Word || app === FileType.Excel || app === FileType.PowerPoint || app === FileType.Visio || app === FileType.OneNote) &&
            this.accessTokenService.hasCAPError(AccessTokenConsumer.Graph)) {
            // These file types use the Graph token to create docs. If we need to show a popup dialog for CAP, open the new document in the same tab to reduce tab switching.
            return LinkTarget.SameFrame;
        }

        if (app === FileType.Fluid || app === FileType.FluidActivityBlueprint) {
            return LinkTarget.SameFrame;
        }
        // For office.com MSA users, there is a possibility that we need to open a popup window to get the token. The browser's popup blocker only allows you to open one popup
        // per user interaction, so we will open the new doc in the same tab to allow the token popup to succeed. There is no popup for PWA.
        return this.isPwa || (this.contextService.sharedContext.defaultLinkTarget === LinkTarget.NewTab &&
            (this.clientStartupConfigService.config.authType === AuthType.OrgId || !candidate.canCreateBlankDocument))
            ? LinkTarget.NewTab
            : LinkTarget.SameFrame;
    }

    private showError(error: ErrorDialogErrorType, fileType: FileType, category: ErrorDialogErrorCategory = ErrorDialogErrorCategory.CreateFile, url?: string) {
        if (this.newTabWindow) { // This won't be set for same-tab navigation
            this.newTabWindow.close();
            this.newTabWindow = null;
        }

        this.errorDialogService.showErrorDialog({ category, error, fileType, inputText: url });
    }

    /**
     * This returns the HTML that mimics the WAC loading page. This will need to be updated whenever the WACs update their loading page
     * so that we are in sync.
     */
    private getLoadingPageHtml(app: FileType): string {
        let loadingHtml = loadingHtmlText;
        loadingHtml = loadingHtml.replace('TITLE', this.locService.CreateNewDocumentLoadingPageTitle);
        loadingHtml = loadingHtml.replace('SPINNERCOLOR', this.getWorkloadSpinnerColor(app));
        loadingHtml = loadingHtml.replace('ONLINEAPPNAME', this.getWorkloadOnlineName(app));

        return loadingHtml;
    }

    private getDocaspxFormHtml(apiUrl: string, token: string): string {
        // Don't URL encode the apiUrl since the Graph API already encodes the doclib/onedrive root URLs.
        return docaspxFormHtmlText.replace('APIURL', apiUrl).replace('ONEDRIVETOKEN', token);
    }

    private getWorkloadOnlineName(app: FileType): string {
        const candidate = this.createLinkCandidates.get(app);
        if (candidate) {
            return candidate.workloadOnlineName;
        }

        return '';
    }

    private getWorkloadSpinnerColor(app: FileType): string {
        const candidate = this.createLinkCandidates.get(app);
        if (candidate && candidate.spinnerColor) {
            return candidate.spinnerColor;
        }

        return '#2b579a'; // Default to word color
    }

    /**
     * @param {FileType.FormsQuiz} fileTypeSecondary - used for when the workload has another FileType which does not have its own workload but uses the same workload as the primary fileType
     * @example Forms is the primary fileType and uses the Forms workload but also has another file type called Quiz that uses the same workload. Quiz is the secondary file type.
     */
    private getCreateLinkForWorkload(workload: Workload, fileTypeSecondary?: FileType, clickLocation?: string): CreateLink {
        const creationFileType = fileTypeSecondary ? fileTypeSecondary : workload.FileType;
        const createCandidate = this.createLinkCandidates.get(creationFileType);
        let workloadId = workload.Id;
        if (!createCandidate) {
            return null;
        }

        let url = workload.Url;
        if (creationFileType === FileType.Sway) {
            url = UrlBuilder.appendQuery(url, 'campaign=OHPCreateNew');
        } else if (creationFileType === FileType.Forms || creationFileType === FileType.FormsQuiz) {
            url = this.formsService.getCreateFormsLink(creationFileType, null, clickLocation);
            workloadId = creationFileType === FileType.FormsQuiz ? workload.Id + 'Quiz' : workload.Id;
        }

        const createLink: CreateLink = {
            ariaLabel: this.locService.format(this.locService.CreateMenuLinkAriaLabelFormat, createCandidate.title),
            title: createCandidate.title,
            fileTypeName: createCandidate.fileTypeName,
            appName: createCandidate.appName,
            icon: FileItem.documentIconFromType(workload.FileType, '32x32'),
            svgButtonBackground: CreateDocumentService.getCreateButtonBackground(creationFileType),
            workload,
            url,
            creationFileType,
            getId: () => workloadId
        };
        return createLink;
    }

    private getCreateLinkForThirdPartyWorkload(workload: Workload): CreateLink {
        return {
            ariaLabel: this.locService.format(this.locService.CreateMenuLinkAriaLabelFormat, workload.Title),
            title: workload.Text,
            fileTypeName: workload.Text, // This needs to be sent
            appName: workload.Text,
            icon: '',
            svgButtonBackground: '',
            workload,
            url: workload.ThirdPartyCreateUrl,
            iconUrl: workload.ThirdPartyIcon,
            creationFileType: FileType.ThirdParty,
            getId: () => workload.Id
        };
    }

    private getCreateLinkForFirstPartyWorkload(workload: Workload): CreateLink {
        return {
            ariaLabel: this.locService.format(this.locService.CreateMenuLinkAriaLabelFormat, workload.Title),
            title: workload.Title,
            fileTypeName: workload.FileTypeNameLocalized,
            appName: workload.Text,
            icon: workload.FileType === FileType.List ? null : workload.FileType === FileType.Designer ? 'Designer_AppIcon' : workload.IconUnicodeCodepointCssClass, // TODO: Remove Designer icon once available in Fluent package
            workload,
            url: workload.Url,
            svgButtonBackground: CreateDocumentService.getCreateButtonBackground(workload.FileType),
            creationFileType: workload.FileType,
            getId: () => workload.Id,
            createFromManifest: true
        };
    }

    private getDocAspxApiUrl(docTypeId: number, origin: string, clickTime: number, templateId?: string, driveId?: string): Promise<Array<string>> {
        return new Promise<Array<string>>((resolve, reject) => {
            // Use NetworkFirst strategy to fetch up-to-date root urls and also update cache
            this.oneDriveService.getMyDriveRootUrl()
                .then(urls => {
                    const doclibUrl = urls.userRoot;
                    const documentsIndex = doclibUrl.lastIndexOf('/Documents');
                    if (documentsIndex === -1) {
                        reject('docliburl not in expected format');
                        return;
                    }

                    const apiRootUrl = doclibUrl.substring(0, documentsIndex);
                    let url = `${apiRootUrl}/_layouts/15/doc.aspx?action=editnew&doctype=${docTypeId}&wdOrigin=${origin}&docliburl=${doclibUrl}&ct=${clickTime}`;
                    if (templateId) {
                        url = url + (driveId ? `&templateDriveId=${driveId}&templateItemId=${templateId}` : `&wdTpl=${templateId}`);
                    }

                    if (templateId || this.createLcidEnabled) {
                        url += `&wdlcid=${this.contextService.sharedContext.officeMarketLcid}`;
                    }

                    const scenarioId = this.capabilitiesService.capabilities.scenarioId;
                    if (scenarioId) {
                        url += `&wdscenarioid=${scenarioId}`;
                    }

                    if (this.capabilitiesService.capabilities.outgoingLoginHintEnabled) {
                        url += `&login_hint=${encodeURIComponent(this.startupConfigService.config.loginHint)}`;
                    }
                    url = url + `&wdPreviousSession=${this.startupConfigService.config.sharedClientStartupContext.sessionId}&wdPreviousSessionSrc=${this.getHarmonySessionSource()}`;

                    const hostname = UrlParser.getOrigin(doclibUrl);
                    resolve([url, hostname]);
                })
                .catch(() => {
                    // TODO: if failure is 404, then user's OneDrive isn't provisioned, so we need to show an error message instead of running any fallback logic.
                    reject('Failed to get doclib url');
                });
        });
    }

    private getOriginParam(docaspxEnabled: boolean, isTemplate: boolean): string {
        if (isTemplate) {
            return this.contextService.getWdOrigin(docaspxEnabled ? WdOriginType.InstantTemplates : WdOriginType.Templates);
        }
        return this.contextService.getWdOrigin(docaspxEnabled ? WdOriginType.InstantNew : WdOriginType.New);
    }

    private launchOfficeApp(app: FileType, webUrl: string = '', targetTab: Window = undefined): Promise<void> {
        if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
            return import('@office-start/launcher-service').then(
                launchSvc =>
                    launchSvc.launchApp({ action: webUrl ? 'OpenDocument' : 'OpenInApp', event, targetTab, appName: FileType[app], url: webUrl, useUrl: true })
                    .then(result => {
                        if (!result.success) {
                            return Promise.reject();
                        }
                    })
            );
        } else {
            return this.localAppLauncherService.launchOfficeApp(FileType[app], webUrl).then(success => {
                if (!success) {
                    return Promise.reject();
                }
            });
        }
    }

    private shouldOpenInUIHost(app: FileType, templateDriveId?: string): boolean {
        return (!templateDriveId || this.capabilitiesService.capabilities.wacHostOrgTemplatesEnabled)
            && this.capabilitiesService.wacHostEnabledForFileType(app)
            && this.wacHostApps
            && this.wacHostApps.has(app);
    }

    private launchWebApp(
        fileUrlModel: FileUrlModel,
        launchAction: LaunchAction,
        openUrl: (url: string, successResultType: CreateDocumentResultType, launchAction: LaunchAction) => void,
        resultHandler: CreateResultHandler,
        resolve: () => void,
        getLatency: () => number,
        bootWithUiHost?: BootNewDocumentUiHostFunction
    ) {
        if (bootWithUiHost) {
            resultHandler && resultHandler({ type: CreateDocumentResultType.GoToBlankDoc, latency: getLatency(), platform: KnownReferralPlatform.Web });
            bootWithUiHost();
            resolve();
        } else {
            openUrl(fileUrlModel.webUrl, CreateDocumentResultType.GoToBlankDoc, launchAction);
        }
    }

    private launchFluidExperience(app: FileType) {
        this.fluidHostContext.hostScenarioStartTime = Date.now();
        this.setHostScenarioName(app);
        this.oneDriveService.getMyDriveRoot(FetchStrategy.NetworkFirst).then(oneDriveInfo => {
            const documentsIndex = oneDriveInfo.webUrl.lastIndexOf('/Documents');
            if (documentsIndex === -1) {
                // TODO: throw error
                return;
            }

            const siteUrl = oneDriveInfo.webUrl.substring(0, documentsIndex);
            const abpId = app === FileType.Fluid ? undefined : 1;
            this.officeRouteService.navigateTo({ route: OfficeRoute.Content, app: FileType.Fluid }, false, { action: 'editnew', abpId, siteUrl, drive: oneDriveInfo.parentReference.driveId });
        });
    }

    private addLoginHint(url: string): string {
        return UrlBuilder.appendLoginHint(url, this.startupConfigService.config.loginHint, this.capabilitiesService.capabilities.outgoingLoginHintEnabled);
    }

    private setHostScenarioName(app: FileType) {
        this.fluidHostContext.hostScenarioLifecycle = 'Create';
        this.fluidHostContext.hostScenarioName = (app === FileType.Fluid) ? 'Canvas' : 'BlueprintCanvas';
        // If user has already opened fluid file and then clicks on Create document, the previous value of hostScenarioEntryPoint(DirectNav) needs to be reset.
        this.fluidHostContext.hostScenarioEntryPoint = '';
    }

    private getMenuOrdering(): Array<FileType> {
        if (this.capabilitiesService.capabilities.fluidFirst) {
            return CreateDocumentService.fluidFirstMenuOrdering;
        } else {
            if (this.capabilitiesService.capabilities.earlierStreamPlacementEnabled) {
                return CreateDocumentService.streamEarlierMenuOrdering;
            } else {
                return CreateDocumentService.menuOrdering;
            }
        }
    }

    private getCombinedMenuOrdering(): Array<FileType> {
        if (this.capabilitiesService.capabilities.fluidFirst) {
            if (this.capabilitiesService.capabilities.earlierStreamPlacementEnabled) {
                return CreateDocumentService.fluidFirstStreamEarlierCombinedMenuOrdering;
            } else {
                return CreateDocumentService.fluidFirstCombinedMenuOrdering;
            }
        } else {
            if (this.capabilitiesService.capabilities.earlierStreamPlacementEnabled) {
                return CreateDocumentService.streamEarlierCombinedMenuOrdering;
            } else {
                return CreateDocumentService.combinedMenuOrdering;
            }
        }
    }
}
